import React from 'react';
import Link from 'next/link';
import {useRouter} from 'next/router';

import {ImageMedia} from '@/lib/types/media';
import {WordpressLink} from '@/features/archiveAsAProduct';
import {WordpressFooter} from '@/features/archiveAsAProduct/types/footer';

import {FooterSocialMedia} from '@/components/footer/FooterSocialMedia';
import {NewsletterButton} from '@/components/footer/NewsletterButton';
import {FooterLogo} from '@/components/footer/FooterLogo';
import FooterPrivacyConsentLink from '@/components/footer/FooterPrivacyConsentLink';

import UofMLogo from '../../../../public/images/icpsr-acronym-long-WHITE-CS6.svg';
import {isImageMedia} from '@/lib/utils/validation';
import {extractFirstQueryValue} from '@/lib/utils/nextjs';

/* ---------- default fall-backs (needed by tests) ---------- */
const defaultSocialMediaLinks = {
  facebook: {
    screenReaderText: 'Facebook',
    href: 'https://www.facebook.com/pages/ICPSR/43191688396',
  },
  instagram: {
    screenReaderText: 'Instagram',
    href: 'https://www.instagram.com/theicpsr/',
  },
  twitter: {
    screenReaderText: 'Twitter',
    href: 'https://www.twitter.com/#!/icpsr',
  },
};

const defaultNewsletter = {
  title: 'Sign up for our newsletter',
  href: 'https://myumi.ch/ICPSRAnnounce',
};

const defaultAccessibilityHref = 'https://myumi.ch/ICPSRAccessibilityWebFoot';
const defaultPrivacyPolicyHref = 'https://myumi.ch/ICPSRPrivacyPolicyWebFoot';

const defaultLogo: ImageMedia = {
  src: UofMLogo,
  height: 40,
  width: 293,
  alt: 'ICPSR - Institute for Social Research - University of Michigan',
  type: 'image',
  subtype: 'svg',
};
/* --------------------------------------------------------- */

export function ArchiveFooter(props: Readonly<WordpressFooter>) {
  const router = useRouter();
  extractFirstQueryValue(router, 'archive'); // kept for side-effects of hook usage

  const hasAdditionalLogos = (props.additionalLogos?.length ?? 0) > 0;

  const logo: ImageMedia = isImageMedia(props.icpsrLogo)
    ? props.icpsrLogo
    : defaultLogo;

  const funderLogoEls =
    props.additionalLogos?.map(({logo, link, logo_height}, idx) => (
      <FooterLogo
        key={`${logo.src}-${idx}`}
        logo={logo}
        link={link || undefined}
        logoHeight={logo_height}
      />
    )) || [];

  const accessibilityHref =
    props.accessibility?.href?.trim() || defaultAccessibilityHref;
  const privacyPolicyHref =
    props.privacyPolicy?.href?.trim() || defaultPrivacyPolicyHref;

  const displayedAdditionalLinks = (props.additionalLinks || []).filter(
    shouldDisplayLink
  );

  const socialLinks =
    Object.keys(props.socialMediaLinks || {}).length > 0
      ? props.socialMediaLinks
      : defaultSocialMediaLinks;

  const newsletter = props.newsletter || defaultNewsletter;

  return (
    <footer className="site-footer" data-testid="icpsr-page-footer">
      <div className="footer-action-bar">
        <div className="container">
          <FooterSocialMedia socialMediaLinks={socialLinks} />

          <ul className="footer-links align-items-center justify-content-center">
            <FooterLink title="Accessibility" href={accessibilityHref} />
            <FooterLink title="Privacy Policy" href={privacyPolicyHref} />
            {displayedAdditionalLinks.map(link => (
              <FooterLink key={link.href} {...link} />
            ))}
          </ul>

          <NewsletterButton newsletter={newsletter} />
          <FooterPrivacyConsentLink />
        </div>
      </div>

      <div className="footer-content container">
        <div className="footer-logos d-flex align-items-md-end">
          <div className="footer-um-logo me-3">
            <a href="https://www.icpsr.umich.edu/">
              <FooterLogo logo={logo} />
            </a>
          </div>

          {hasAdditionalLogos && (
            <div className="footer-archive-logos d-flex flex-wrap gap-3">
              {funderLogoEls}
            </div>
          )}
        </div>

        {!hasAdditionalLogos && props.textBlurb && (
          <div
            className="footer-extra"
            dangerouslySetInnerHTML={{__html: props.textBlurb}}
          />
        )}

        <div className="footer-copyright">
          <p>
            &copy; {new Date().getFullYear()} The{' '}
            <a href="http://regents.umich.edu/">
              Regents of the University of Michigan
            </a>
            . ICPSR is part of the{' '}
            <a href="http://www.isr.umich.edu/">
              Institute for Social Research
            </a>{' '}
            at the <a href="http://www.umich.edu/">University of Michigan</a>.
          </p>
        </div>
      </div>
    </footer>
  );
}

function FooterLink(props: Readonly<WordpressLink & {title: string}>) {
  if (!props.href?.trim()) return null;
  return (
    <li>
      <Link href={props.href} target={props.target}>
        {props.title}
      </Link>
    </li>
  );
}

function shouldDisplayLink(link?: WordpressLink): boolean {
  return !!link?.href?.trim() && !!link.title?.trim();
}

export const __testing__ = {
  defaultSocialMediaLinks,
  defaultLogo,
  defaultNewsletter,
  defaultAccessibilityHref,
  defaultPrivacyPolicyHref,
};

export default ArchiveFooter;
